// Axios API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// React
import { useEffect, useState } from 'react'

// UI
import Box from '@/components/ui/Box'
import ScrollableScreen from '@/components/ui/ScrollableScreen'
import Badge from '@/components/ui/Badge'
import LocationSelect from '@/components/ui/LocationSelect'

// Next
import { Link } from 'react-router-dom'

// Classnames
import classNames from 'classnames'

// Lodash
import { uniq, sortBy } from 'lodash'

// Dayjs
import dayjs from 'dayjs'

// Mantine
import { Select } from '@mantine/core'

export default function IndexNew() {
    const { requestGet } = useAxiosApi()

    const [groups, setGroups] = useState(null)

    const loadGroups = async () => {
        const { data } = await requestGet('/shopify/products-groups')
        setGroups(data)
    }

    // Default to Atelier Paris (id 6) for everyone, changeable via the select.
    const [locationId, setLocationId] = useState('6')
    const [shouldDisplayVariants, setShouldDisplayVariants] = useState(true)

    useEffect(() => {
        loadGroups()
    }, [])

    const breadcrumbs = [
        {
            label: 'Produits Shopify',
            href: '/products/shopify',
        },
    ]

    const rightSection = (
        <div className="flex items-center gap-2">
            <Select
                value={
                    shouldDisplayVariants ? 'display_variants' : 'hide_variants'
                }
                onChange={(value) =>
                    setShouldDisplayVariants(value === 'display_variants')
                }
                data={[
                    { value: 'hide_variants', label: 'Variantes masquées' },
                    { value: 'display_variants', label: 'Variantes affichées' },
                ]}
            />

            <LocationSelect value={locationId} onChange={setLocationId} />
        </div>
    )

    if (!groups) {
        return (
            <ScrollableScreen breadcrumbs={breadcrumbs}>
                <div className="p-5 flex flex-col gap-5 @container">
                    <div className="h-[300px] w-full bg-gray-200 rounded-lg animate-pulse" />
                    <div className="h-[500px] w-full bg-gray-200 rounded-lg animate-pulse" />
                </div>
            </ScrollableScreen>
        )
    }

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs} rightSection={rightSection}>
            <div className="p-5 flex flex-col gap-5 @container">
                {groups &&
                    groups.map((group) => (
                        <Box
                            key={group.id}
                            title={group.name}
                            rightSection={
                                <span className="text-sm text-gray-500">
                                    {group.products.length} produits
                                </span>
                            }
                        >
                            <div className="grid grid-cols-1 @4xl:grid-cols-2 @7xl:grid-cols-3 gap-5">
                                {group.products.map((product) => {
                                    const fulfill_at_dates = uniq(
                                        sortBy(
                                            product.variants
                                                .filter(
                                                    (variant) =>
                                                        variant.fulfill_at &&
                                                        variant.availabilities.find(
                                                            (a) =>
                                                                a.location_id.toString() ===
                                                                locationId
                                                        )?.shopify_buyable ===
                                                            true
                                                )
                                                .map((variant) => {
                                                    return variant.fulfill_at
                                                })
                                        )
                                    )

                                    const verbose_fulfill_at_dates =
                                        fulfill_at_dates
                                            .map((date) => {
                                                return dayjs(date).format('ll')
                                            })
                                            .join(', ')

                                    return (
                                        <Link
                                            to={`/products/shopify/${product.id}`}
                                            key={product.id}
                                            className="flex flex-col border p-5 rounded-lg hover:bg-gray-50 cursor-pointer"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex flex-col gap-1">
                                                    <span className="font-medium">
                                                        {product.title}
                                                    </span>

                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-orange-500">
                                                            {
                                                                verbose_fulfill_at_dates
                                                            }
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="w-[50px] aspect-square shrink-0">
                                                    {product.image_url && (
                                                        <img
                                                            src={
                                                                product.image_url +
                                                                '&width=200'
                                                            }
                                                            className="w-full h-full object-contain"
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            {shouldDisplayVariants && (
                                                <div className="flex flex-col mt-3">
                                                    <div className="flex gap-5 items-start">
                                                        <div className="flex flex-col w-full gap-1">
                                                            <div className="grid grid-cols-[1fr_30px_30px] gap-2 text-sm w-full text-gray-500">
                                                                <span className="truncate">
                                                                    Variante
                                                                </span>

                                                                <span className="text-right">
                                                                    Shopi.
                                                                </span>

                                                                <span className="text-right">
                                                                    BO
                                                                </span>
                                                            </div>

                                                            {product.variants.map(
                                                                (variant) => {
                                                                    const availabilitiesAtLocation =
                                                                        variant.availabilities.find(
                                                                            (
                                                                                a
                                                                            ) =>
                                                                                a.location_id.toString() ===
                                                                                locationId
                                                                        )

                                                                    const isAvailableShopify =
                                                                        availabilitiesAtLocation?.shopify_available >
                                                                        0

                                                                    const isAvailableBO =
                                                                        availabilitiesAtLocation?.available_b2c >
                                                                        0

                                                                    const isShopifyBuyable =
                                                                        availabilitiesAtLocation?.shopify_buyable ??
                                                                        false

                                                                    let shopifyColor =
                                                                        isShopifyBuyable
                                                                            ? 'text-green-500'
                                                                            : 'text-red-500'

                                                                    shopifyColor =
                                                                        variant.fulfill_at &&
                                                                        isShopifyBuyable
                                                                            ? 'text-orange-500'
                                                                            : shopifyColor

                                                                    return (
                                                                        <div
                                                                            key={
                                                                                variant.id
                                                                            }
                                                                            className="grid grid-cols-[1fr_30px_30px] gap-2 text-sm w-full"
                                                                        >
                                                                            <span
                                                                                className={classNames(
                                                                                    'truncate pr-5',
                                                                                    shopifyColor
                                                                                )}
                                                                            >
                                                                                {
                                                                                    variant.title
                                                                                }
                                                                            </span>

                                                                            <span
                                                                                className={classNames(
                                                                                    'text-right font-mono',
                                                                                    shopifyColor
                                                                                )}
                                                                            >
                                                                                {variant.inventory_policy ===
                                                                                    'CONTINUE' &&
                                                                                locationId ===
                                                                                    '1'
                                                                                    ? '∞'
                                                                                    : availabilitiesAtLocation?.shopify_available}
                                                                            </span>

                                                                            <span
                                                                                className={classNames(
                                                                                    'text-right font-mono',
                                                                                    {
                                                                                        'text-green-500':
                                                                                            1 ===
                                                                                            1,
                                                                                        'text-red-500':
                                                                                            !isAvailableBO,
                                                                                    }
                                                                                )}
                                                                            >
                                                                                {
                                                                                    availabilitiesAtLocation?.available_b2c
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                }
                                                            )}
                                                        </div>
                                                    </div>

                                                    {!product.should_push_inventory_to_shopify && (
                                                        <span className="text-purple-500 mt-5 text-sm text-center">
                                                            Envoi de
                                                            l'inventaire BO
                                                            désactivé
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </Link>
                                    )
                                })}
                            </div>
                        </Box>
                    ))}
            </div>
        </ScrollableScreen>
    )
}
